import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Input, Select } from '@/components/ui/Input';
import { EmptyState, Pagination, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { SectionSpinner } from '@/components/ui/Spinner';
import { auditApi } from '@/services/endpoints/transactions';
import { usersApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { formatDateTime } from '@/lib/dateUtils';
import type { AuditAction } from '@/domain/types';

const ACTION_KEYS: Record<AuditAction, string> = {
  'invoice.created': 'invoiceCreated',
  'invoice.updated': 'invoiceUpdated',
  'report.downloaded': 'reportDownloaded',
};

export function AuditLogPage(): JSX.Element {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [userId, setUserId] = useState<number | ''>('');
  const [action, setAction] = useState<AuditAction | ''>('');

  const params = {
    page,
    pageSize: 25,
    from: from || undefined,
    to: to || undefined,
    userId: userId === '' ? undefined : userId,
    action: action === '' ? undefined : action,
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.auditLogs.list(params),
    queryFn: () => auditApi.list(params),
    placeholderData: (previous) => previous,
  });

  // Back-office headcount is small, so the whole list doubles as the filter's options.
  const { data: userOptions } = useQuery({
    queryKey: queryKeys.users.list({ pageSize: 200 }),
    queryFn: () => usersApi.list({ pageSize: 200 }),
  });

  return (
    <div className="space-y-4">
      <PageHeader title={t('auditLog.title')} description={t('auditLog.description')} />

      <Card>
        <div className="flex flex-wrap items-end gap-3 p-4">
          <Input
            label={t('common.from')}
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              setPage(1);
            }}
            className="w-auto"
          />
          <Input
            label={t('common.to')}
            type="date"
            value={to}
            onChange={(event) => {
              setTo(event.target.value);
              setPage(1);
            }}
            className="w-auto"
          />
          <Select
            label={t('auditLog.user')}
            value={userId}
            onChange={(event) => {
              setUserId(event.target.value === '' ? '' : Number(event.target.value));
              setPage(1);
            }}
            className="w-auto"
            options={[
              { value: '', label: t('auditLog.allUsers') },
              ...(userOptions?.rows ?? []).map((user) => ({ value: user.id, label: user.fullName })),
            ]}
          />
          <Select
            label={t('auditLog.action')}
            value={action}
            onChange={(event) => {
              setAction(event.target.value as AuditAction | '');
              setPage(1);
            }}
            className="w-auto"
            options={[
              { value: '', label: t('auditLog.allActions') },
              ...(Object.keys(ACTION_KEYS) as AuditAction[]).map((value) => ({
                value,
                label: t(`auditLog.actions.${ACTION_KEYS[value]}` as never),
              })),
            ]}
          />
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <SectionSpinner />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState icon={<ShieldCheck className="h-6 w-6" />} title={t('common.noResults')} />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>{t('auditLog.dateTime')}</TH>
                  <TH>{t('auditLog.user')}</TH>
                  <TH>{t('auditLog.action')}</TH>
                  <TH>{t('auditLog.details')}</TH>
                </TR>
              </THead>

              <TBody>
                {data.rows.map((entry) => (
                  <TR key={entry.id}>
                    <TD className="whitespace-nowrap text-slate-700">
                      {formatDateTime(entry.occurredAt)}
                    </TD>
                    <TD className="font-medium text-slate-900">
                      {entry.userId ? entry.userName : t('auditLog.system')}
                    </TD>
                    <TD>{entry.actionLabel}</TD>
                    <TD className="text-xs text-slate-500">
                      {entry.entityType}
                      {entry.entityId ? ` #${entry.entityId}` : ''}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}
